import type { Drawing, DrawingKind, DrawingStyle } from "lightweight-charts-drawing"
import { opacityOf, opaque, withOpacity } from "../../ui/color.ts"

// What TradingView's drawing settings offer for one drawing, from its kind and
// style: which rows the Style and Text tabs show, and where each edit goes.
// Plain functions, so they're tested without React or a chart.

/** Tools whose text is the drawing's own (edited in place); other tools carry an optional label in their style. */
const TEXT_TOOLS: ReadonlySet<DrawingKind> = new Set<DrawingKind>([
  "text",
  "note",
  "pin",
  "comment",
  "price-note",
  "signpost",
  "callout"
])

/**
 * Shapes whose renderer draws the drawing's own `text` inside them (wrapped in a
 * rectangle, centred in a circle or ellipse), as the text tools do.
 */
const SHAPES_WITH_TEXT: ReadonlySet<DrawingKind> = new Set<DrawingKind>(["rectangle", "circle", "ellipse"])

/** Lines, channels and ranges whose renderer draws `style.text` as a label. */
const LABELLED: ReadonlySet<DrawingKind> = new Set<DrawingKind>([
  "trend-line",
  "ray",
  "extended-line",
  "arrow",
  "info-line",
  "horizontal-line",
  "vertical-line",
  "horizontal-ray",
  "parallel-channel",
  "disjoint-channel",
  "flat-top-bottom",
  "arrow-marker",
  "price-range",
  "date-range",
  "date-and-price-range"
])

/** A text tool: the library opens its editor itself (on placing it, and on double-click). */
export const isTextTool = (drawing: Drawing): boolean => TEXT_TOOLS.has(drawing.kind)

/**
 * Where a drawing's text lives, if it can show any: the drawing (text tools, and shapes
 * that hold text inside), its style (a line's label), or nowhere (a triangle, a fib, …).
 * Other kinds with a font or text colour keep a label in their style, as before.
 */
export const textLocation = (drawing: Drawing): "drawing" | "style" | null =>
  TEXT_TOOLS.has(drawing.kind) || SHAPES_WITH_TEXT.has(drawing.kind)
    ? "drawing"
    : LABELLED.has(drawing.kind) || drawing.style.fontSize !== undefined || drawing.style.textColor !== undefined
    ? "style"
    : null

export const FONT_SIZES: ReadonlyArray<number> = [10, 11, 12, 14, 16, 20, 24, 28, 32, 40]

export interface DrawingSettingsModel {
  /** The Text tab: the text tools, shapes that hold text, and the lines that carry a label (trend line, ray, …). */
  readonly hasText: boolean
  /** Where the text lives: the drawing (text tools) or its style (a line's label). */
  readonly textTarget: "drawing" | "style"
  /** The style key the text colour is: text tools without a separate text colour use their main colour. */
  readonly textColorKey: "textColor" | "color"
  /** A background colour row (shapes, notes, callouts). */
  readonly hasBackground: boolean
  /** A shape (rectangle, circle, triangle, …): its line is the border around a filled inside. */
  readonly isShape: boolean
  /** A border row for text tools that draw one (text, pin, comment, callout, …). */
  readonly hasBorder: boolean
  /** Label alignment selects (a line's label). */
  readonly hasAlignment: boolean
  /** The Style tab's line row: everything but a plain text tool has a line. */
  readonly hasLine: boolean
}

export const settingsModel = (drawing: Drawing): DrawingSettingsModel => {
  const style = drawing.style
  const textTool = TEXT_TOOLS.has(drawing.kind)
  const hasBackground = style.backgroundColor !== undefined
  const location = textLocation(drawing)
  return {
    hasText: location !== null,
    textTarget: location ?? "style",
    textColorKey: style.textColor !== undefined || !textTool ? "textColor" : "color",
    hasBackground,
    isShape: hasBackground && !textTool,
    hasBorder: textTool && (style.drawBorder !== undefined || style.borderColor !== undefined),
    hasAlignment: !textTool && (style.horzLabelsAlign !== undefined || style.vertLabelsAlign !== undefined),
    hasLine: drawing.kind !== "text"
  }
}

// The library draws a fill as `backgroundColor` at (100 − transparency)% (TradingView's
// model), and text tools drop any alpha inside the colour. So the picker's one colour with
// opacity maps onto an opaque colour plus `transparency`; an alpha saved inside the colour
// by older versions still shows (the two multiply) and is folded into transparency on edit.

/** The fill as the picker shows it: the drawn colour at the drawn opacity. */
export const fillColorOf = (drawing: Drawing): string => {
  const base = drawing.style.backgroundColor ?? drawing.style.color
  return withOpacity(base, opacityOf(base) * (100 - (drawing.style.transparency ?? 0)) / 100)
}

/** The style patch for a fill picked as one colour with opacity. */
export const fillPatch = (color: string): Partial<DrawingStyle> => ({
  backgroundColor: opaque(color),
  transparency: Math.round((1 - opacityOf(color)) * 100),
  fillBackground: true
})

/** The drawing's text, wherever it lives. */
export const textOf = (drawing: Drawing): string =>
  (settingsModel(drawing).textTarget === "drawing" ? (drawing as { text?: string }).text : drawing.style.text) ?? ""

/** The drawing with new text, wherever it lives. */
export const withText = (drawing: Drawing, text: string): Drawing =>
  settingsModel(drawing).textTarget === "drawing"
    ? ({ ...drawing, text } as Drawing)
    : { ...drawing, style: { ...drawing.style, text } }

/** The drawing with part of its style changed. */
export const withStyle = (drawing: Drawing, patch: Partial<DrawingStyle>): Drawing => ({
  ...drawing,
  style: { ...drawing.style, ...patch }
})

/** The colour the text shows in (a line's label falls back to the line's colour, as the renderer does). */
export const textColorOf = (drawing: Drawing): string => {
  const model = settingsModel(drawing)
  return model.textColorKey === "color" ? drawing.style.color : drawing.style.textColor ?? drawing.style.color
}
